/**
 * MUI Select for choosing a recent RMA that carries the tracking number and
 * shipping cost for the current inbound return.
 *
 * Fetches up to 5 recent RMAs with tracking for the given site group on
 * mount; a "fetch all" link expands to 100. Replicates legacy
 * RecentRmasWithTrackingSelectField behavior with no Formsy dependency.
 */

import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { fetchRmasWithTrackingForSiteGroup } from '@sophocles/entities/Rma';

/**
 * @param {object} props
 * @param {number} props.siteGroupId
 * @param {number|string|null} props.value      - selected RMA id
 * @param {function} props.onChange             - (newValue) => void
 * @param {import('immutable').Map|null} [props.selectedRma] - current saved RMA (pinned top)
 * @param {string} [props.label]
 */
export default function RecentTrackingRmaSelect({ siteGroupId, value, onChange, selectedRma = null, label }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [rmas, setRmas] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const resolvedLabel =
    label ?? t('rma.rmaTrackingShippingLabel', 'RMA that includes the tracking # and shipping cost');

  const doFetch = limit => {
    dispatch(fetchRmasWithTrackingForSiteGroup(siteGroupId, limit)).then(res => {
      const results = res.get ? res.get('results', null) : null;
      if (results) {
        setRmas(
          results
            .toArray()
            .map(r => ({ id: r.get('id'), label: `${r.get('rma_number', '')} - ${r.get('status_display', '')}` })),
        );
      }
    });
  };

  useEffect(() => {
    if (siteGroupId) doFetch(5);
  }, [siteGroupId]);

  const handleFetchAll = () => {
    setShowAll(true);
    doFetch(100);
  };

  // Prepend the currently-saved RMA so it always appears even if not in the recent list
  let options = [...rmas];
  if (selectedRma) {
    const selectedId = selectedRma.get ? selectedRma.get('id', -1) : (selectedRma.id ?? -1);
    const label = selectedRma.get
      ? `${selectedRma.get('rma_number', '')} - ${selectedRma.get('status_display', '')}`
      : t('rma.rmaNumberShort', 'RMA #{{id}}', { id: selectedId });
    options = [{ id: selectedId, label }, ...options.filter(o => o.id !== selectedId)];
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="body2" fontWeight="bold">
          {resolvedLabel}
        </Typography>
        {!showAll && (
          <Typography variant="caption" sx={{ color: 'primary.main', cursor: 'pointer' }} onClick={handleFetchAll}>
            {t('rma.clickToFetchAll', '(Click to fetch all)')}
          </Typography>
        )}
        {showAll && (
          <Typography variant="caption" color="text.secondary">
            {t('rma.showingAll', '(showing all)')}
          </Typography>
        )}
      </Box>
      <FormControl fullWidth size="small">
        <InputLabel shrink>{resolvedLabel}</InputLabel>
        <Select value={value ?? ''} label={resolvedLabel} notched onChange={e => onChange(e.target.value || null)}>
          <MenuItem value="">
            <em>{t('rma.none', 'None')}</em>
          </MenuItem>
          {options.map(o => (
            <MenuItem key={o.id} value={o.id}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}
